/**
 * Performance Instrumentation
 *
 * Tracks critical performance metrics for app launch:
 * - TTFB (Time to First Byte)
 * - FCP (First Contentful Paint)
 * - LCP (Largest Contentful Paint)
 * - Auth call duration
 * - Today summary fetch duration
 */

interface PerformanceMetrics {
  ttfb?: number;
  fcp?: number;
  lcp?: number;
  authDuration?: number;
  todaySummaryFetch?: number;
  appLaunchTotal?: number;
}

let performanceMetrics: PerformanceMetrics = {};
let launchStartTime: number | null = null;

/**
 * Initialize performance tracking
 * Should be called as early as possible in app initialization
 */
export function initPerformanceTracking(): void {
  launchStartTime = performance.now();

  // Track TTFB if available (requires navigation timing)
  if ("performance" in window && "getEntriesByType" in performance) {
    try {
      const navigation = performance.getEntriesByType(
        "navigation",
      )[0] as PerformanceNavigationTiming;
      if (navigation) {
        performanceMetrics.ttfb =
          navigation.responseStart - navigation.requestStart;
      }
    } catch (error) {
      console.warn("[Performance] Failed to track TTFB:", error);
    }
  }

  // Track FCP (First Contentful Paint)
  if ("PerformanceObserver" in window) {
    try {
      const fcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const fcpEntry = entries.find(
          (entry) => entry.name === "first-contentful-paint",
        );
        if (fcpEntry && "startTime" in fcpEntry) {
          performanceMetrics.fcp = fcpEntry.startTime;
          logPerformanceMetrics();
        }
      });
      fcpObserver.observe({ entryTypes: ["paint"] });
    } catch (error) {
      console.warn("[Performance] Failed to track FCP:", error);
    }

    // Track LCP (Largest Contentful Paint)
    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        if (lastEntry && "renderTime" in lastEntry) {
          performanceMetrics.lcp = lastEntry.renderTime as number;
        }
      });
      lcpObserver.observe({ entryTypes: ["largest-contentful-paint"] });
    } catch (error) {
      console.warn("[Performance] Failed to track LCP:", error);
    }
  }
}

/**
 * Mark auth call start
 */
export function markAuthStart(): void {
  if (!launchStartTime) {
    launchStartTime = performance.now();
  }
  performanceMetrics.authDuration = performance.now();
}

/**
 * Mark auth call complete
 */
export function markAuthComplete(): void {
  if (performanceMetrics.authDuration) {
    performanceMetrics.authDuration =
      performance.now() - performanceMetrics.authDuration;
  }
}

/**
 * Mark today summary fetch start
 */
export function markTodaySummaryFetchStart(): number {
  return performance.now();
}

/**
 * Mark today summary fetch complete
 */
export function markTodaySummaryFetchComplete(startTime: number): void {
  performanceMetrics.todaySummaryFetch = performance.now() - startTime;
}

/**
 * Mark app launch complete
 */
export function markAppLaunchComplete(): void {
  if (launchStartTime) {
    performanceMetrics.appLaunchTotal = performance.now() - launchStartTime;
    logPerformanceMetrics();
  }
}

/**
 * Log all performance metrics
 */
function logPerformanceMetrics(): void {
  // In production, send to analytics/error tracking service
  // Example: sendToAnalytics('app_launch_performance', performanceMetrics);
}

/**
 * Get current performance metrics
 */
export function getPerformanceMetrics(): PerformanceMetrics {
  return { ...performanceMetrics };
}

/**
 * Reset performance metrics (for testing)
 */
export function resetPerformanceMetrics(): void {
  performanceMetrics = {};
  launchStartTime = null;
}
